import { useState } from 'react'
import { Settings, Zap, AlertCircle, CheckCircle, Activity, RotateCw, Save } from 'lucide-react'

export default function AIConfigPage() {
  const [config, setConfig] = useState({
    model: 'mixtral-8x7b-32768',
    temperature: 0.7,
    maxTokens: 1024,
    systemPrompt: 'You are a helpful AI assistant for a 24/7 customer support center. Be professional, empathetic, and concise.',
    enableSentimentAnalysis: true,
    enableIntentDetection: true,
    enableEscalationDetection: true,
    enableAiSummarization: true,
  })

  const [metrics, setMetrics] = useState({
    totalRequests: 4247,
    totalErrors: 156,
    errorRate: 3.67,
    avgResponseTime: 642,
    tokensPerSecond: 287,
    health: 'healthy'
  })

  const [showSaved, setShowSaved] = useState(false)
  const [selectedTab, setSelectedTab] = useState('general') // general, models, monitoring, audit

  const availableModels = [
    {
      name: 'mixtral-8x7b-32768',
      display: 'Mixtral 8x7B (Recommended)',
      tps: 300,
      contextWindow: 32768,
      description: 'Fastest and most balanced. Best for real-time support.'
    },
    {
      name: 'llama2-70b-4096',
      display: 'LLaMA 2 70B',
      tps: 40,
      contextWindow: 4096,
      description: 'Most capable but slower. Best for complex queries.'
    },
    {
      name: 'gemma-7b-it',
      display: 'Gemma 7B',
      tps: 150,
      contextWindow: 8192,
      description: 'Lightweight and fast. Good balance of speed and quality.'
    }
  ]

  const auditLogs = [
    {
      id: 1,
      timestamp: '2024-04-18 14:22',
      changedBy: 'admin@cybertection.com',
      changeType: 'Model Change',
      oldValue: 'llama2-70b-4096',
      newValue: 'mixtral-8x7b-32768',
      reason: 'Improved response times for customer support'
    },
    {
      id: 2,
      timestamp: '2024-04-18 10:15',
      changedBy: 'admin@cybertection.com',
      changeType: 'Parameter Update',
      oldValue: 'temperature: 0.5',
      newValue: 'temperature: 0.7',
      reason: 'Better response variety requested'
    },
    {
      id: 3,
      timestamp: '2024-04-17 16:45',
      changedBy: 'system',
      changeType: 'Feature Toggle',
      oldValue: 'enable_escalation_detection: false',
      newValue: 'enable_escalation_detection: true',
      reason: 'Automatic escalation activated'
    }
  ]

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = () => {
    setShowSaved(true)
    setTimeout(() => setShowSaved(false), 3000)
  }

  const getHealthColor = (health) => {
    switch(health) {
      case 'healthy': return 'text-green-600'
      case 'degraded': return 'text-orange-600'
      case 'critical': return 'text-red-600'
      default: return 'text-blue-200'
    }
  }

  const getHealthBg = (health) => {
    switch(health) {
      case 'healthy': return 'bg-green-500/20'
      case 'degraded': return 'bg-orange-500/20'
      case 'critical': return 'bg-red-500/20'
      default: return 'bg-slate-700/30'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">AI Configuration</h1>
        <p className="text-blue-200 mt-1">Monitor and adjust Groq AI model settings and performance</p>
      </div>

      {/* Saved Notification */}
      {showSaved && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="text-green-400" size={20} />
          <p className="text-green-200">Configuration saved successfully!</p>
        </div>
      )}

      {/* Health Status Card */}
      <div className={`card border-2 ${
        metrics.health === 'healthy' ? 'border-green-200' :
        metrics.health === 'degraded' ? 'border-orange-200' :
        'border-red-200'
      }`}>
        <div className="card-body">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Activity className={`${getHealthColor(metrics.health)}`} size={28} />
              <div>
                <h3 className="font-semibold text-white">AI System Health</h3>
                <p className={`text-sm font-medium ${getHealthColor(metrics.health)} capitalize`}>
                  Status: {metrics.health}
                </p>
              </div>
            </div>
            <button className="text-blue-200 hover:text-white">
              <RotateCw size={20} />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-blue-200">Error Rate</p>
              <p className={`text-2xl font-bold ${metrics.errorRate > 10 ? 'text-orange-600' : 'text-green-600'}`}>
                {metrics.errorRate.toFixed(2)}%
              </p>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                <div
                  className={`h-2 rounded-full ${metrics.errorRate > 10 ? 'bg-orange-500' : 'bg-green-500'}`}
                  style={{ width: `${metrics.errorRate}%` }}
                ></div>
              </div>
            </div>
            <div>
              <p className="text-sm text-blue-200">Avg Response</p>
              <p className="text-2xl font-bold text-cyber-600">{metrics.avgResponseTime}ms</p>
              <p className="text-xs text-blue-300/70 mt-2">per request</p>
            </div>
            <div>
              <p className="text-sm text-blue-200">Throughput</p>
              <p className="text-2xl font-bold text-cyber-600">{metrics.tokensPerSecond}</p>
              <p className="text-xs text-blue-300/70 mt-2">tokens/sec</p>
            </div>
            <div>
              <p className="text-sm text-blue-200">Total Requests</p>
              <p className="text-2xl font-bold text-white">{metrics.totalRequests.toLocaleString()}</p>
              <p className="text-xs text-blue-300/70 mt-2">{metrics.totalErrors} errors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-blue-500/30">
        <div className="flex space-x-8">
          {['general', 'models', 'monitoring', 'audit'].map(tab => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`py-3 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                selectedTab === tab
                  ? 'border-cyber-600 text-cyber-600'
                  : 'border-transparent text-blue-200 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* General Settings */}
      {selectedTab === 'general' && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-white">Model Parameters</h3>
          </div>
          <div className="card-body space-y-6">
            {/* Current Model Display */}
            <div>
              <p className="text-sm text-blue-200 mb-3">Current Model</p>
              <div className="bg-slate-800/50 border border-blue-500/30 rounded-lg p-4">
                <p className="font-semibold text-white">{config.model}</p>
                <p className="text-sm text-blue-200 mt-1">Mixtral 8x7B (Recommended)</p>
              </div>
            </div>

            {/* Temperature */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Temperature: {config.temperature.toFixed(2)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={config.temperature}
                onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-blue-300/70 mt-2">
                Lower (0.0) = more focused, Higher (2.0) = more creative
              </p>
            </div>

            {/* Max Tokens */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Max Tokens: {config.maxTokens}
              </label>
              <input
                type="number"
                value={config.maxTokens}
                onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
                className="input-base"
                min="256"
                max="4096"
              />
              <p className="text-xs text-blue-300/70 mt-2">
                Maximum length of AI responses (256-4096)
              </p>
            </div>

            {/* System Prompt */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                System Prompt
              </label>
              <textarea
                value={config.systemPrompt}
                onChange={(e) => handleConfigChange('systemPrompt', e.target.value)}
                className="input-base"
                rows="5"
              />
              <p className="text-xs text-blue-300/70 mt-2">
                Instructions that guide the AI's behavior in all conversations
              </p>
            </div>

            {/* Feature Toggles */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-white mb-4">AI Features</h4>
              <div className="space-y-3">
                {[
                  { key: 'enableSentimentAnalysis', label: 'Sentiment Analysis', desc: 'Detect customer emotion (positive/negative)' },
                  { key: 'enableIntentDetection', label: 'Intent Detection', desc: 'Identify customer intent (support/sales/complaint)' },
                  { key: 'enableEscalationDetection', label: 'Escalation Detection', desc: 'Automatically flag frustrated customers' },
                  { key: 'enableAiSummarization', label: 'AI Summarization', desc: 'Generate summaries of conversations' },
                ].map(feature => (
                  <label key={feature.key} className="flex items-start space-x-3 p-3 rounded hover:bg-slate-700/30 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config[feature.key]}
                      onChange={(e) => handleConfigChange(feature.key, e.target.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-white">{feature.label}</p>
                      <p className="text-sm text-blue-200">{feature.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-6 border-t">
              <button onClick={handleSave} className="btn-primary flex items-center space-x-2">
                <Save size={18} />
                <span>Save Configuration</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Model Selection */}
      {selectedTab === 'models' && (
        <div className="space-y-4">
          {availableModels.map(model => (
            <div
              key={model.name}
              className={`card cursor-pointer border-2 transition-all ${
                config.model === model.name
                  ? 'border-blue-500/60 bg-slate-800/70'
                  : 'border-blue-500/30 hover:border-blue-500/40'
              }`}
              onClick={() => handleConfigChange('model', model.name)}
            >
              <div className="card-body">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-white">{model.display}</h3>
                    <p className="text-sm text-blue-200 mt-1">{model.description}</p>
                  </div>
                  {config.model === model.name && (
                    <CheckCircle className="text-cyber-600" size={24} />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <p className="text-blue-200">Tokens/sec</p>
                    <p className="font-semibold text-white">{model.tps}</p>
                  </div>
                  <div>
                    <p className="text-blue-200">Context Window</p>
                    <p className="font-semibold text-white">{model.contextWindow.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Monitoring */}
      {selectedTab === 'monitoring' && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="font-semibold text-white">Performance Metrics</h3>
            <button className="text-cyber-600 hover:text-cyber-700">
              <RotateCw size={20} />
            </button>
          </div>
          <div className="card-body space-y-6">
            {/* Charts would go here */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-white mb-4">Response Time Trend</h4>
                <div className="bg-slate-700/30 rounded-lg h-48 flex items-center justify-center">
                  <p className="text-blue-300/70">Chart visualization coming soon</p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-4">Error Rate Trend</h4>
                <div className="bg-slate-700/30 rounded-lg h-48 flex items-center justify-center">
                  <p className="text-blue-300/70">Chart visualization coming soon</p>
                </div>
              </div>
            </div>

            {/* Detailed Metrics Table */}
            <div className="border-t pt-6">
              <h4 className="font-semibold text-white mb-4">Detailed Metrics</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-blue-500/30">
                      <th className="text-left py-2 px-3 font-medium text-blue-200">Metric</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-200">Value</th>
                      <th className="text-right py-2 px-3 font-medium text-blue-200">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-blue-500/20">
                      <td className="py-3 px-3 text-white">Total Requests</td>
                      <td className="text-right py-3 px-3 font-semibold">{metrics.totalRequests.toLocaleString()}</td>
                      <td className="text-right py-3 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-blue-500/20">
                      <td className="py-3 px-3 text-white">Total Errors</td>
                      <td className="text-right py-3 px-3 font-semibold">{metrics.totalErrors}</td>
                      <td className="text-right py-3 px-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          metrics.errorRate > 10 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {metrics.errorRate > 10 ? 'Warning' : 'Healthy'}
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-blue-500/20">
                      <td className="py-3 px-3 text-white">Error Rate</td>
                      <td className="text-right py-3 px-3 font-semibold">{metrics.errorRate.toFixed(2)}%</td>
                      <td className="text-right py-3 px-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          metrics.errorRate > 10 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {metrics.errorRate > 10 ? 'Watch' : 'Good'}
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-blue-500/20">
                      <td className="py-3 px-3 text-white">Avg Response Time</td>
                      <td className="text-right py-3 px-3 font-semibold">{metrics.avgResponseTime}ms</td>
                      <td className="text-right py-3 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Optimal
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-3 text-white">Throughput</td>
                      <td className="text-right py-3 px-3 font-semibold">{metrics.tokensPerSecond} tokens/sec</td>
                      <td className="text-right py-3 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Excellent
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log */}
      {selectedTab === 'audit' && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-white">Configuration Change History</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {auditLogs.map(log => (
                <div key={log.id} className="border border-blue-500/30 rounded-lg p-4 hover:bg-slate-700/30">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-white">{log.changeType}</p>
                      <p className="text-sm text-blue-200">{log.timestamp} by {log.changedBy}</p>
                    </div>
                    <p className="text-sm text-blue-300/70">{log.reason}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div className="bg-red-500/20 p-2 rounded">
                      <p className="text-blue-200 text-xs">Old Value</p>
                      <p className="text-white font-mono text-sm">{log.oldValue}</p>
                    </div>
                    <div className="bg-green-500/20 p-2 rounded">
                      <p className="text-blue-200 text-xs">New Value</p>
                      <p className="text-white font-mono text-sm">{log.newValue}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
